import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Bullseye,
  Button,
  Card,
  CardBody,
  CardTitle,
  Flex,
  FlexItem,
  Grid,
  GridItem,
  Label,
  LabelGroup,
  Spinner,
} from '@patternfly/react-core';
import {
  QueueManagerReadyzResponse,
  QueueManagerWorkerReadyzResponse,
  parseQueueManagerReadyzResponse,
  parseQueueManagerWorkerReadyzResponse,
} from 'src/contracts/health';
import { useAuthedFetch } from 'src/hooks/useAuthedFetch';

type EndpointHealthState<TPayload> = {
  payload: TPayload | null;
  httpStatus: number | null;
  error: string | null;
};

type QueueManagerServerHealthState = {
  readyz: EndpointHealthState<QueueManagerReadyzResponse>;
  workerReadyz: EndpointHealthState<QueueManagerWorkerReadyzResponse>;
  fetchedAt: string | null;
};

type HealthResponseParserResult<TPayload> = {
  ok: boolean;
  value?: TPayload;
  err?: { message: string };
};

type HealthResponseParser<TPayload> = (raw: unknown) => HealthResponseParserResult<TPayload>;

type ServerHealthCardProps = {
  health: QueueManagerServerHealthState;
  loading: boolean;
  onRefresh: () => void;
};

const RAW_QUEUE_BASE_URL = process.env.REACT_APP_QUEUE_URL || '';
const QUEUE_BASE_URL = RAW_QUEUE_BASE_URL.replace(/\/$/, '');
const HEALTH_POLL_INTERVAL_MS = 5000;

const formatDateTime = (value?: string | null) => {
  if (!value) {
    return '—';
  }
  const parsedValue = new Date(value);
  const parsedTime = parsedValue.getTime();
  if (Number.isNaN(parsedTime)) {
    return '—';
  }
  return parsedValue.toLocaleString();
};

const formatNumber = (value?: number | null, fractionDigits = 1) => {
  const isMissingNumber = typeof value !== 'number';
  if (isMissingNumber) {
    return '—';
  }
  const isFiniteNumber = Number.isFinite(value);
  if (!isFiniteNumber) {
    return '—';
  }
  return value.toLocaleString(undefined, {
    maximumFractionDigits: fractionDigits,
    minimumFractionDigits: 0,
  });
};

const formatMilliseconds = (value?: number | null) => {
  const formattedNumber = formatNumber(value);
  if (formattedNumber === '—') {
    return formattedNumber;
  }
  return `${formattedNumber} ms`;
};

const formatSeconds = (value?: number | null) => {
  const formattedNumber = formatNumber(value);
  if (formattedNumber === '—') {
    return formattedNumber;
  }
  return `${formattedNumber} s`;
};

const toStatusLabelColor = (status?: string | null) => {
  if (status === 'ready') {
    return 'green';
  }
  if (status === 'not_ready') {
    return 'red';
  }
  return 'grey';
};

const toCheckLabelColor = (ok: boolean) => {
  return ok ? 'green' : 'red';
};

const createEndpointHealthState = <TPayload,>(): EndpointHealthState<TPayload> => {
  return {
    payload: null,
    httpStatus: null,
    error: null,
  };
};

const createServerHealthState = (): QueueManagerServerHealthState => {
  return {
    readyz: createEndpointHealthState<QueueManagerReadyzResponse>(),
    workerReadyz: createEndpointHealthState<QueueManagerWorkerReadyzResponse>(),
    fetchedAt: null,
  };
};

const StatRow = ({ label, value }: { label: string; value: React.ReactNode }) => {
  return (
    <div
      style={{
        alignItems: 'center',
        display: 'flex',
        gap: '12px',
        justifyContent: 'space-between',
      }}
    >
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
};

const renderReadyzChecks = (payload: QueueManagerReadyzResponse | null) => {
  const checks = payload?.checks ?? [];
  const hasChecks = checks.length > 0;
  if (!hasChecks) {
    return <Label color="grey">No readiness checks returned</Label>;
  }
  return (
    <LabelGroup style={{ marginTop: 8 }}>
      {checks.map((check) => {
        const detailSuffix = check.detail ? ` · ${check.detail}` : '';
        const latencySuffix = check.latency_ms != null ? ` · ${formatMilliseconds(check.latency_ms)}` : '';
        const labelText = `${check.name}${latencySuffix}${detailSuffix}`;
        const labelColor = toCheckLabelColor(check.ok);
        return (
          <Label key={check.name} color={labelColor}>
            {labelText}
          </Label>
        );
      })}
    </LabelGroup>
  );
};

const renderWorkerChecks = (payload: QueueManagerWorkerReadyzResponse | null) => {
  const checks = payload?.checks ?? [];
  const hasChecks = checks.length > 0;
  if (!hasChecks) {
    return <Alert isInline variant="warning" title="No worker health checks returned" />;
  }
  return (
    <div style={{ display: 'grid', gap: 12, marginTop: 12 }}>
      {checks.map((check) => {
        const labelColor = toCheckLabelColor(check.ok);
        const detailText = check.detail || 'healthy';
        return (
          <Card key={check.name} isCompact>
            <CardBody>
              <Flex
                alignItems={{ default: 'alignItemsCenter' }}
                justifyContent={{ default: 'justifyContentSpaceBetween' }}
              >
                <FlexItem>
                  <strong>{check.name}</strong>
                </FlexItem>
                <FlexItem>
                  <Label color={labelColor}>{check.ok ? 'healthy' : 'unhealthy'}</Label>
                </FlexItem>
              </Flex>
              <div style={{ display: 'grid', gap: 6, marginTop: 12 }}>
                <StatRow label="Last Seen" value={formatDateTime(check.last_seen_at)} />
                <StatRow label="Age" value={formatSeconds(check.age_seconds)} />
                <StatRow label="Stale After" value={formatSeconds(check.stale_after_seconds)} />
                <StatRow label="Detail" value={detailText} />
              </div>
            </CardBody>
          </Card>
        );
      })}
    </div>
  );
};

const ServerHealthCard = ({ health, loading, onRefresh }: ServerHealthCardProps) => {
  const readyzPayload = health.readyz.payload;
  const readyzStatus = readyzPayload?.status ?? null;
  const readyzStatusColor = toStatusLabelColor(readyzStatus);
  const workerPayload = health.workerReadyz.payload;
  const workerStatus = workerPayload?.status ?? null;
  const workerStatusColor = toStatusLabelColor(workerStatus);
  const readyzResponseStatus = health.readyz.httpStatus ?? '—';
  const workerResponseStatus = health.workerReadyz.httpStatus ?? '—';
  const fetchedAtText = formatDateTime(health.fetchedAt);

  return (
    <Card>
      <CardTitle>Queue Manager Server Health</CardTitle>
      <CardBody>
        <Flex gap={{ default: 'gapMd' }} alignItems={{ default: 'alignItemsCenter' }}>
          <FlexItem>
            <Button onClick={onRefresh} isDisabled={loading}>
              Refresh Health
            </Button>
          </FlexItem>
          <FlexItem>
            <Label color="blue">Polling every {HEALTH_POLL_INTERVAL_MS / 1000}s</Label>
          </FlexItem>
          <FlexItem>
            <Label color="green">Last update: {fetchedAtText}</Label>
          </FlexItem>
        </Flex>

        {loading && !health.fetchedAt && (
          <Bullseye style={{ minHeight: 120 }}>
            <Spinner />
          </Bullseye>
        )}

        <Grid hasGutter style={{ marginTop: 16 }}>
          <GridItem span={6}>
            <Card isCompact>
              <CardTitle>API /readyz</CardTitle>
              <CardBody>
                {health.readyz.error && (
                  <Alert isInline variant="danger" title="Failed to load /readyz">
                    {health.readyz.error}
                  </Alert>
                )}

                {!health.readyz.error && (
                  <>
                    <div style={{ display: 'grid', gap: 8 }}>
                      <StatRow
                        label="Status"
                        value={<Label color={readyzStatusColor}>{readyzStatus ?? 'no_data'}</Label>}
                      />
                      <StatRow label="HTTP" value={readyzResponseStatus} />
                      <StatRow label="Generated" value={formatDateTime(readyzPayload?.ts)} />
                    </div>
                    <div style={{ marginTop: 16 }}>
                      <Label>Dependency Checks</Label>
                      {renderReadyzChecks(readyzPayload)}
                    </div>
                  </>
                )}
              </CardBody>
            </Card>
          </GridItem>

          <GridItem span={6}>
            <Card isCompact>
              <CardTitle>Workers /worker-readyz</CardTitle>
              <CardBody>
                {health.workerReadyz.error && (
                  <Alert isInline variant="danger" title="Failed to load /worker-readyz">
                    {health.workerReadyz.error}
                  </Alert>
                )}

                {!health.workerReadyz.error && (
                  <>
                    <div style={{ display: 'grid', gap: 8 }}>
                      <StatRow
                        label="Status"
                        value={<Label color={workerStatusColor}>{workerStatus ?? 'no_data'}</Label>}
                      />
                      <StatRow label="HTTP" value={workerResponseStatus} />
                      <StatRow label="Generated" value={formatDateTime(workerPayload?.ts)} />
                    </div>
                    {renderWorkerChecks(workerPayload)}
                  </>
                )}
              </CardBody>
            </Card>
          </GridItem>
        </Grid>
      </CardBody>
    </Card>
  );
};

const QueueManagerHealth = () => {
  const authedFetch = useAuthedFetch();
  const [serverHealth, setServerHealth] = useState<QueueManagerServerHealthState>(() => createServerHealthState());
  const [serverHealthLoading, setServerHealthLoading] = useState(false);
  const hasQueueBase = Boolean(QUEUE_BASE_URL);

  const readHealthEndpoint = useCallback(
    async <TPayload,>(
      path: string,
      parseResponse: HealthResponseParser<TPayload>,
    ): Promise<EndpointHealthState<TPayload>> => {
      try {
        const endpointUrl = `${QUEUE_BASE_URL}${path}`;
        const response = await authedFetch(endpointUrl);
        const raw = await response.json();
        const parsed = parseResponse(raw);
        if (!parsed.ok) {
          return {
            payload: null,
            httpStatus: response.status,
            error: parsed.err?.message || 'Health response contract validation failed',
          };
        }
        return {
          payload: parsed.value ?? null,
          httpStatus: response.status,
          error: null,
        };
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load health endpoint';
        return {
          payload: null,
          httpStatus: null,
          error: errorMessage,
        };
      }
    },
    [authedFetch],
  );

  const loadServerHealth = useCallback(async () => {
    if (!hasQueueBase) {
      return;
    }
    setServerHealthLoading(true);
    const readyzPromise = readHealthEndpoint('/readyz', parseQueueManagerReadyzResponse);
    const workerReadyzPromise = readHealthEndpoint('/worker-readyz', parseQueueManagerWorkerReadyzResponse);
    const [readyz, workerReadyz] = await Promise.all([readyzPromise, workerReadyzPromise]);
    const fetchedAt = new Date().toISOString();
    setServerHealth({
      readyz,
      workerReadyz,
      fetchedAt,
    });
    setServerHealthLoading(false);
  }, [hasQueueBase, readHealthEndpoint]);

  const refreshHealth = useCallback(() => {
    void loadServerHealth();
  }, [loadServerHealth]);

  useEffect(() => {
    if (!hasQueueBase) {
      return;
    }

    void loadServerHealth();
    const intervalId = window.setInterval(() => {
      void loadServerHealth();
    }, HEALTH_POLL_INTERVAL_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [hasQueueBase, loadServerHealth]);

  if (!hasQueueBase) {
    return (
      <Card>
        <CardBody>
          <Alert isInline variant="danger" title="Queue Manager URL missing">
            Set `REACT_APP_QUEUE_URL` to point at the Queue Manager.
          </Alert>
        </CardBody>
      </Card>
    );
  }

  return (
    <ServerHealthCard health={serverHealth} loading={serverHealthLoading} onRefresh={refreshHealth} />
  );
};

export default QueueManagerHealth;
